/**
 * Smart Entry Scanner
 * Tìm điểm vào lệnh thông minh: trend sạch + nền co hẹp + cầu đỡ rõ
 * T+ (3-7 phiên) & Swing (2-6 tuần)
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Tooltip,
  Collapse, Slider, Skeleton, alpha, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import {
  StairsOutlined, Radar, TrendingUp, TuneOutlined, ExpandMore,
  Speed, Waves, TrendingDown, Block,
  PlayArrow, CheckCircle, Cancel, Schedule, FiberManualRecord,
  TrackChanges,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { easyStepAPI, smartEntryTrackerAPI } from '../services/api';

// ── Tag colors ──
const TAG_COLORS = {
  TREND_STRONG: '#10b981', TREND_OK: '#3b82f6', BASE_TIGHT: '#8b5cf6',
  ORDERLY_BASE: '#7c3aed', POCKET_PIVOT: '#f59e0b', ABSORPTION: '#06b6d4',
  NEAR_HH20: '#ec4899', VOL_RECOVERY: '#14b8a6', HIGHER_LOWS: '#0ea5e9',
  SMA_SUPPORT: '#6366f1', CLIMAX_STALL: '#e11d48', OVERHEATED: '#f97316',
  DIST_CLIMAX: '#ef4444', FAIL_BO: '#dc2626', HH_HL: '#059669',
};

// ── Stat tile ──
const StatTile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    position: 'relative', overflow: 'hidden',
    borderRadius: 2.5, border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff', p: '14px 16px',
    transition: 'all 0.2s',
    '&:hover': { borderColor: alpha(color, 0.3), boxShadow: `0 4px 16px ${alpha(color, 0.08)}`, transform: 'translateY(-1px)' },
  }}>
    <Box sx={{ position: 'absolute', top: 0, right: 0, width: 64, height: 64, background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)` }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{
        width: 38, height: 38, borderRadius: 2, flexShrink: 0,
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 3px 8px ${alpha(color, 0.25)}`,
      }}>
        <Icon sx={{ fontSize: 20, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: typeof value === 'string' && value.length > 5 ? '1.1rem' : '1.75rem', fontWeight: 900, color, lineHeight: 1.2, fontFamily: typeof value === 'number' ? 'monospace' : 'inherit' }}>
          {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.4 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

// ── Candidate grid ──
const COLS = '72px 100px 48px 75px 60px 1fr 120px';
const hSx = { fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.02em' };

const CandidateGrid = ({ data, variant = 'tplus', t }) => {
  const isTplus = variant === 'tplus';

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '0.88rem', color: '#94a3b8' }}>
          {t('smartEntry.noCandidates')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: COLS, gap: 1.5,
        px: 2.5, py: 1, borderBottom: '1px solid #e2e8f0',
      }}>
        <Typography sx={hSx}>{t('smartEntry.colTicker')}</Typography>
        <Typography sx={hSx}>{t('smartEntry.colSector')}</Typography>
        <Typography sx={{ ...hSx, textAlign: 'right' }}>{t('smartEntry.colScore')}</Typography>
        <Typography sx={{ ...hSx, textAlign: 'right' }}>{t('smartEntry.colPrice')}</Typography>
        <Typography sx={{ ...hSx, textAlign: 'right' }}>% MA20</Typography>
        <Typography sx={hSx}>{t('smartEntry.colSignal')}</Typography>
        <Typography sx={{ ...hSx, textAlign: 'right' }}>{t('smartEntry.colEntryStop')}</Typography>
      </Box>

      {/* Rows */}
      {data.map((item, rowIdx) => {
        const entry = isTplus ? item.entry_tplus : item.entry_swing;
        const stop = isTplus ? item.stop_tplus : item.stop_swing;
        const riskPct = entry && stop ? ((entry - stop) / entry * 100).toFixed(1) : null;
        const scoreColor = item.score >= 60 ? '#10b981' : item.score >= 48 ? '#1e293b' : '#94a3b8';
        const distColor = item.dist_sma20_pct <= 2 ? '#10b981' : item.dist_sma20_pct <= 5 ? '#1e293b' : '#ef4444';

        // Build score tooltip text
        const scoreTip = [
          `Trend ${Math.round(item.score_trend)}/25`,
          `Base ${Math.round(item.score_base)}/25`,
          `Momentum ${Math.round(item.score_momentum)}/20`,
          `Demand ${Math.round(item.score_demand)}/20`,
          item.score_risk > 0 ? `Risk -${Math.round(item.score_risk)}` : null,
        ].filter(Boolean).join(' · ');

        return (
          <Box
            key={item.ticker}
            sx={{
              display: 'grid', gridTemplateColumns: COLS, gap: 1.5,
              px: 2.5, py: 1.3, alignItems: 'center',
              borderBottom: '1px solid #f1f5f9',
              transition: 'background 0.15s',
              '&:hover': { bgcolor: '#f8fafc' },
            }}
          >
            {/* Ticker */}
            <Box>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                {item.ticker}
              </Typography>
              {item.label === 'both' && (
                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', mt: 0.15 }}>T+ & Swing</Typography>
              )}
            </Box>

            {/* Sector */}
            <Tooltip title={item.sector_name?.vi || ''} arrow placement="top" disableHoverListener={!item.sector_name?.vi || item.sector_name.vi.length < 12}>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.sector_name?.vi || '—'}
              </Typography>
            </Tooltip>

            {/* Score */}
            <Tooltip title={scoreTip} arrow placement="left">
              <Typography sx={{ textAlign: 'right', fontSize: '0.92rem', fontWeight: 800, color: scoreColor, fontFamily: 'monospace', cursor: 'default' }}>
                {Math.round(item.score)}
              </Typography>
            </Tooltip>

            {/* Price + ATR */}
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b', fontFamily: 'monospace', lineHeight: 1.2 }}>
                {(item.close / 1000).toFixed(1)}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', lineHeight: 1.2 }}>
                ATR {item.atrp14.toFixed(1)}%
              </Typography>
            </Box>

            {/* Distance to SMA20 */}
            <Typography sx={{
              textAlign: 'right', fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace', color: distColor,
            }}>
              {item.dist_sma20_pct > 0 ? '+' : ''}{item.dist_sma20_pct.toFixed(1)}%
            </Typography>

            {/* Tags — plain colored text, wrapped */}
            <Box sx={{ lineHeight: 1.6 }}>
              {(item.tags || []).map((tag, i) => {
                const tagColor = TAG_COLORS[tag] || '#64748b';
                const tagLabel = t(`smartEntry.tags.${tag}`, tag);
                return (
                  <Typography
                    key={i}
                    component="span"
                    sx={{ fontSize: '0.76rem', fontWeight: 600, color: tagColor, whiteSpace: 'nowrap' }}
                  >
                    {i > 0 && <Typography component="span" sx={{ fontSize: '0.76rem', color: '#cbd5e1', mx: 0.3 }}>·</Typography>}
                    {tagLabel}
                  </Typography>
                );
              })}
            </Box>

            {/* Entry / Stop */}
            <Typography sx={{ textAlign: 'right', fontSize: '0.85rem', fontFamily: 'monospace', color: '#475569', lineHeight: 1.2 }}>
              <Typography component="span" sx={{ fontSize: 'inherit', fontFamily: 'inherit', fontWeight: 700, color: '#10b981' }}>
                {entry ? (entry / 1000).toFixed(1) : '—'}
              </Typography>
              <Typography component="span" sx={{ fontSize: 'inherit', color: '#d1d5db', mx: 0.5 }}>/</Typography>
              <Typography component="span" sx={{ fontSize: 'inherit', fontFamily: 'inherit', fontWeight: 600, color: '#ef4444' }}>
                {stop ? (stop / 1000).toFixed(1) : '—'}
              </Typography>
              {riskPct && (
                <Typography component="span" sx={{ fontSize: '0.7rem', fontFamily: 'inherit', color: '#94a3b8', ml: 0.5 }}>
                  {riskPct}%
                </Typography>
              )}
            </Typography>
          </Box>
        );
      })}

      {/* Footer */}
      <Box sx={{ px: 2.5, py: 0.8, borderTop: '1px solid #f1f5f9' }}>
        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
          {t('smartEntry.candidateCount', { count: data.length })}
        </Typography>
      </Box>
    </Box>
  );
};

// ── Status helpers ──
const STATUS_CFG = {
  open: { key: 'statusOpen', color: '#3b82f6', Icon: FiberManualRecord },
  stopped: { key: 'statusStopped', color: '#ef4444', Icon: Cancel },
  target_hit: { key: 'statusTargetHit', color: '#10b981', Icon: CheckCircle },
  expired: { key: 'statusExpired', color: '#f59e0b', Icon: Schedule },
};

const fmtPct = (v) => v != null ? `${v >= 0 ? '+' : ''}${Number(v).toFixed(1)}%` : '–';
const fmtPrice = (v) => v != null ? Number(v).toLocaleString('vi-VN') : '–';

// ── Tracker Panel ──
const TrackerPanel = ({ recs, stats, loading, filter, onFilterChange, onRecord, recording, t }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} height={42} sx={{ mb: 0.5, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  const winRate = stats?.overall?.win_rate;
  const profitFactor = stats?.overall?.profit_factor;
  const totalClosed = stats?.overall?.total_closed || 0;

  return (
    <Box>
      {/* Stats bar + filter */}
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          {winRate != null && (
            <Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 600 }}>
              Win rate <Box component="span" sx={{ color: winRate >= 50 ? '#10b981' : '#ef4444', fontWeight: 800, fontFamily: 'monospace' }}>{winRate.toFixed(0)}%</Box>
            </Typography>
          )}
          {profitFactor != null && (
            <Typography sx={{ fontSize: '0.8rem', color: '#475569', fontWeight: 600 }}>
              PF <Box component="span" sx={{ color: profitFactor >= 1.2 ? '#10b981' : '#ef4444', fontWeight: 800, fontFamily: 'monospace' }}>{profitFactor.toFixed(2)}</Box>
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{totalClosed} {t('smartEntry.tracker.closed')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {[
              { key: 'all', label: t('smartEntry.tracker.filterAll') },
              { key: 'open', label: t('smartEntry.tracker.filterOpen') },
              { key: 'closed', label: t('smartEntry.tracker.filterClosed') },
              { key: 'tplus', label: 'T+' },
              { key: 'swing', label: 'Swing' },
            ].map(f => {
              const active = filter === f.key;
              return (
                <Typography
                  key={f.key}
                  onClick={() => onFilterChange(f.key)}
                  sx={{
                    fontSize: '0.78rem', fontWeight: active ? 700 : 500,
                    color: active ? '#8b5cf6' : '#94a3b8',
                    cursor: 'pointer', px: 1, py: 0.3,
                    borderBottom: active ? '2px solid #8b5cf6' : '2px solid transparent',
                    transition: 'all 0.15s',
                    '&:hover': { color: '#8b5cf6' },
                  }}
                >
                  {f.label}
                </Typography>
              );
            })}
          </Box>
          <Button
            size="small" variant="outlined"
            onClick={onRecord} disabled={recording}
            startIcon={recording ? <CircularProgress size={14} /> : <PlayArrow sx={{ fontSize: 16 }} />}
            sx={{ px: 1.5, py: 0.4, fontSize: '0.75rem', fontWeight: 700, textTransform: 'none', borderRadius: 1.5, borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { borderColor: '#7c3aed', bgcolor: alpha('#8b5cf6', 0.04) } }}
          >
            {t('smartEntry.tracker.recordToday')}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      {recs.length === 0 ? (
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.88rem', color: '#94a3b8' }}>{t('smartEntry.tracker.noRecommendations')}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', bgcolor: '#fafbfc', borderBottom: '1px solid #e2e8f0', py: 0.8, letterSpacing: '0.02em' } }}>
                <TableCell>{t('smartEntry.tracker.colDate')}</TableCell>
                <TableCell>{t('smartEntry.tracker.colTicker')}</TableCell>
                <TableCell>{t('smartEntry.tracker.colType')}</TableCell>
                <TableCell align="right">{t('smartEntry.tracker.colEntry')}</TableCell>
                <TableCell align="right">{t('smartEntry.tracker.colStop')}</TableCell>
                <TableCell align="right">{t('smartEntry.tracker.colTarget')}</TableCell>
                <TableCell align="center">{t('smartEntry.tracker.colStatus')}</TableCell>
                <TableCell align="right">{t('smartEntry.tracker.colPnl')}</TableCell>
                <TableCell align="right">{t('smartEntry.tracker.colMaxGain')}</TableCell>
                <TableCell align="right">{t('smartEntry.tracker.colMaxDd')}</TableCell>
                <TableCell align="right">{t('smartEntry.tracker.colDays')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recs.map((r, i) => {
                const stCfg = STATUS_CFG[r.status] || STATUS_CFG.open;
                const st = { ...stCfg, label: t(`smartEntry.tracker.${stCfg.key}`) };
                const pnlColor = r.current_return_pct > 0 ? '#10b981' : r.current_return_pct < 0 ? '#ef4444' : '#64748b';
                return (
                  <TableRow key={i} sx={{ '&:hover': { bgcolor: '#fafbfc' }, '& td': { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' } }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem !important', color: '#64748b' }}>
                      {r.scan_date ? format(new Date(r.scan_date), 'dd/MM') : '–'}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{r.ticker}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{
                        display: 'inline-flex', px: 0.8, py: 0.15, borderRadius: 0.8,
                        bgcolor: r.signal_type === 'tplus' ? alpha('#3b82f6', 0.08) : alpha('#10b981', 0.08),
                      }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: r.signal_type === 'tplus' ? '#3b82f6' : '#10b981' }}>
                          {r.signal_type === 'tplus' ? 'T+' : 'Swing'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtPrice(r.entry_price)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#ef4444' }}>{fmtPrice(r.stop_price)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#10b981' }}>{fmtPrice(r.target_price)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.15, borderRadius: 0.8, bgcolor: alpha(st.color, 0.08) }}>
                        <st.Icon sx={{ fontSize: 10, color: st.color }} />
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: st.color }}>{st.label}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700, color: pnlColor }}>
                      {fmtPct(r.current_return_pct)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#10b981', fontSize: '0.75rem !important' }}>
                      {fmtPct(r.max_gain_pct)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#ef4444', fontSize: '0.75rem !important' }}>
                      {r.max_drawdown_pct != null ? `${Number(r.max_drawdown_pct).toFixed(1)}%` : '–'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: '0.75rem !important' }}>
                      {r.days_tracked ?? '–'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

// ── Main page ──
const EasyStepScanner = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastScan, setLastScan] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [configOpen, setConfigOpen] = useState(false);
  const [config, setConfig] = useState({
    min_score: 35,
    tplus_min_score: 35,
    swing_min_score: 35,
    min_avg_value_20: 10e9,
    min_price: 5000,
    top_k: 30,
  });

  const fetchResults = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await easyStepAPI.scan(config);
      setData(resp.data);
      setLastScan(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResults(); }, []); // eslint-disable-line

  const tplusList = data?.tplus_candidates || [];
  const swingList = data?.swing_candidates || [];
  const marketRegime = data?.config?.market_regime || 'unknown';

  const breadthPct = data?.config?.breadth_pct;
  const breadthDir = data?.config?.breadth_direction;
  const vnClose = data?.config?.vn_close;
  const vnDrop = data?.config?.vn_drop_from_peak;
  const dirArrow = breadthDir === 'improving' ? '\u2191' : breadthDir === 'declining' ? '\u2193' : '';
  const breadthLabel = breadthPct != null ? `Breadth ${Math.round(breadthPct)}%${dirArrow}` : '';
  const vnLabel = vnClose ? `VN ${Math.round(vnClose)}` : '';
  const dropLabel = vnDrop ? `(${vnDrop > 0 ? '+' : ''}${vnDrop.toFixed(1)}%)` : '';

  const REGIME_CFG = {
    bull: { label: t('smartEntry.regime.bull'), color: '#10b981', Icon: TrendingUp },
    healthy: { label: t('smartEntry.regime.healthy'), color: '#3b82f6', Icon: TrendingUp },
    recovering: { label: t('smartEntry.regime.recovering'), color: '#f59e0b', Icon: TrendingUp },
    declining: { label: t('smartEntry.regime.declining'), color: '#ef4444', Icon: TrendingDown },
    stress: { label: t('smartEntry.regime.stress'), color: '#ef4444', Icon: TrendingDown },
    panic: { label: t('smartEntry.regime.panic'), color: '#dc2626', Icon: Block },
    unknown: { label: t('smartEntry.regime.unknown'), color: '#94a3b8', Icon: Waves },
  };
  const regime = REGIME_CFG[marketRegime] || REGIME_CFG.unknown;

  // ── Tracker state ──
  const [trackerRecs, setTrackerRecs] = useState([]);
  const [trackerStats, setTrackerStats] = useState(null);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [trackerFilter, setTrackerFilter] = useState('all');
  const [recording, setRecording] = useState(false);

  const fetchTracker = useCallback(async () => {
    setTrackerLoading(true);
    try {
      const params = { limit: 100 };
      if (trackerFilter === 'open') params.status = 'open';
      if (trackerFilter === 'tplus') params.signal_type = 'tplus';
      if (trackerFilter === 'swing') params.signal_type = 'swing';
      const [recResp, statsResp] = await Promise.all([
        smartEntryTrackerAPI.getRecommendations(params),
        smartEntryTrackerAPI.getStats(),
      ]);
      let d = recResp.data?.data || [];
      if (trackerFilter === 'closed') d = d.filter(r => r.status !== 'open');
      setTrackerRecs(d);
      setTrackerStats(statsResp.data?.data || null);
    } catch (err) { console.error(err); }
    finally { setTrackerLoading(false); }
  }, [trackerFilter]);

  const handleRecord = async () => {
    setRecording(true);
    try { await smartEntryTrackerAPI.recordNow(); fetchTracker(); }
    catch (err) { console.error(err); }
    finally { setRecording(false); }
  };

  useEffect(() => { if (activeTab === 2) fetchTracker(); }, [activeTab, fetchTracker]);

  const trackerCount = trackerRecs.length;

  const TABS = [
    { key: 'tplus', label: t('smartEntry.tabTplus'), color: '#3b82f6', Icon: Speed, count: tplusList.length },
    { key: 'swing', label: t('smartEntry.tabSwing'), color: '#10b981', Icon: TrendingUp, count: swingList.length },
    { key: 'tracker', label: t('smartEntry.tabTracker'), color: '#8b5cf6', Icon: TrackChanges, count: trackerCount },
  ];

  return (
    <Box sx={{ p: 2.5, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha('#667eea', 0.3)}`,
          }}>
            <StairsOutlined sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                {t('smartEntry.title')}
              </Typography>
              {lastScan && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.8, py: 0.25, borderRadius: 1, bgcolor: alpha('#10b981', 0.08), border: `1px solid ${alpha('#10b981', 0.15)}` }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>
                    {format(lastScan, 'HH:mm:ss')}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500, lineHeight: 1.3, mt: 0.3 }}>
              {t('smartEntry.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained" size="small"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Radar sx={{ fontSize: 18 }} />}
          onClick={fetchResults} disabled={loading}
          sx={{
            px: 2.5, py: 0.8, fontSize: '0.85rem', fontWeight: 700,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: `0 3px 8px ${alpha('#667eea', 0.3)}`,
            textTransform: 'none', borderRadius: 2,
            '&:hover': { boxShadow: `0 4px 14px ${alpha('#667eea', 0.4)}` },
          }}
        >
          {loading ? t('smartEntry.scanning') : t('smartEntry.scan')}
        </Button>
      </Box>

      {/* Config panel */}
      <Box sx={{ mb: 2, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', overflow: 'hidden' }}>
        <Box onClick={() => setConfigOpen(!configOpen)} sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1,
          cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }, transition: 'background 0.15s',
        }}>
          <TuneOutlined sx={{ fontSize: 18, color: '#64748b' }} />
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>
            {t('smartEntry.filterConfig')}
          </Typography>
          <ExpandMore sx={{ fontSize: 20, color: '#94a3b8', transform: configOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
        </Box>
        <Collapse in={configOpen}>
          <Box sx={{ px: 2, pb: 2, pt: 0.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {[
              { label: t('smartEntry.minScore'), value: config.min_score, key: 'min_score', min: 30, max: 90, step: 5, color: '#667eea' },
              { label: t('smartEntry.liquidity'), value: config.min_avg_value_20 / 1e9, key: 'min_avg_value_20', min: 1, max: 50, step: 1, color: '#3b82f6', transform: v => v * 1e9 },
              { label: t('smartEntry.minPrice'), value: config.min_price / 1000, key: 'min_price', min: 1, max: 50, step: 1, color: '#10b981', transform: v => v * 1000 },
              { label: t('smartEntry.maxResults'), value: config.top_k, key: 'top_k', min: 10, max: 50, step: 5, color: '#f59e0b' },
            ].map(s => (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>{s.label}</Typography>
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 900, color: s.color, fontFamily: 'monospace', minWidth: 28, textAlign: 'right' }}>
                  {Math.round(s.value)}
                </Typography>
                <Slider
                  value={s.value} onChange={(e, v) => setConfig(prev => ({ ...prev, [s.key]: s.transform ? s.transform(v) : v }))}
                  min={s.min} max={s.max} step={s.step} size="small"
                  sx={{ flex: 1, minWidth: 60, '& .MuiSlider-thumb': { width: 14, height: 14, bgcolor: s.color }, '& .MuiSlider-track': { bgcolor: s.color } }}
                />
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>

      {/* Stat tiles */}
      {loading && !data ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
          {[0, 1, 2, 3].map(i => (
            <Box key={i} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '14px 16px' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: 2, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width={60} height={12} sx={{ mb: 0.8 }} />
                  <Skeleton width={40} height={26} sx={{ mb: 0.4 }} />
                  <Skeleton width={100} height={11} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
          <StatTile label={t('smartEntry.market')} value={regime.label} sub={[breadthLabel, vnLabel, dropLabel].filter(Boolean).join(' · ')} color={regime.color} Icon={regime.Icon} />
          <StatTile label={t('smartEntry.scanned')} value={data?.total_scanned || 0} sub={t('smartEntry.scannedSub')} color="#64748b" Icon={Waves} />
          <StatTile label={t('smartEntry.tplusCandidates')} value={tplusList.length} sub={t('smartEntry.tplusSub')} color="#3b82f6" Icon={Speed} />
          <StatTile label={t('smartEntry.swingCandidates')} value={swingList.length} sub={t('smartEntry.swingSub')} color="#10b981" Icon={TrendingUp} />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: alpha('#ef4444', 0.06), border: `1px solid ${alpha('#ef4444', 0.15)}` }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#ef4444' }}>{error}</Typography>
        </Box>
      )}

      {/* Tab bar */}
      <Box sx={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', mb: 0 }}>
        {TABS.map((tab, idx) => {
          const active = activeTab === idx;
          return (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(idx)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.8, px: 2.5, py: 1.2, cursor: 'pointer',
                borderBottom: active ? `2.5px solid ${tab.color}` : '2.5px solid transparent', mb: '-2px',
                transition: 'all 0.15s', '&:hover': { bgcolor: alpha(tab.color, 0.04) },
              }}
            >
              <tab.Icon sx={{ fontSize: 18, color: active ? tab.color : '#94a3b8' }} />
              <Typography sx={{ fontSize: '0.88rem', fontWeight: active ? 800 : 600, color: active ? tab.color : '#64748b' }}>
                {tab.label}
              </Typography>
              <Box sx={{
                display: 'inline-flex', px: 0.8, py: 0.15, borderRadius: 1,
                bgcolor: active ? alpha(tab.color, 0.1) : '#f1f5f9',
                minWidth: 24, justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, fontFamily: 'monospace', color: active ? tab.color : '#94a3b8' }}>
                  {tab.count}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Tab content */}
      <Box sx={{ borderRadius: '0 0 10px 10px', border: '1px solid #e2e8f0', borderTop: 'none', bgcolor: '#fff', overflow: 'hidden' }}>
        {loading ? (
          <Box>
            {/* Skeleton header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: COLS, gap: 1.5, px: 2.5, py: 1, borderBottom: '1px solid #e2e8f0' }}>
              {['45px', '60px', '30px', '40px', '35px', '80px', '70px'].map((w, i) => (
                <Skeleton key={i} width={w} height={12} />
              ))}
            </Box>
            {/* Skeleton rows */}
            {[...Array(8)].map((_, i) => (
              <Box key={i} sx={{ display: 'grid', gridTemplateColumns: COLS, gap: 1.5, px: 2.5, py: 1.3, alignItems: 'center', borderBottom: '1px solid #f1f5f9' }}>
                <Box>
                  <Skeleton width={42} height={14} sx={{ mb: 0.3 }} />
                  <Skeleton width={52} height={9} />
                </Box>
                <Skeleton width={70} height={13} />
                <Skeleton width={28} height={15} sx={{ ml: 'auto' }} />
                <Box sx={{ textAlign: 'right' }}>
                  <Skeleton width={38} height={14} sx={{ ml: 'auto', mb: 0.3 }} />
                  <Skeleton width={48} height={9} sx={{ ml: 'auto' }} />
                </Box>
                <Skeleton width={40} height={13} sx={{ ml: 'auto' }} />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Skeleton width={55} height={13} />
                  <Skeleton width={45} height={13} />
                  <Skeleton width={35} height={13} />
                </Box>
                <Skeleton width={90} height={13} sx={{ ml: 'auto' }} />
              </Box>
            ))}
          </Box>
        ) : activeTab === 0 ? (
          <CandidateGrid data={tplusList} variant="tplus" t={t} />
        ) : activeTab === 1 ? (
          <CandidateGrid data={swingList} variant="swing" t={t} />
        ) : (
          <TrackerPanel
            recs={trackerRecs} stats={trackerStats} loading={trackerLoading}
            filter={trackerFilter} onFilterChange={setTrackerFilter}
            onRecord={handleRecord} recording={recording} t={t}
          />
        )}
      </Box>
    </Box>
  );
};

export default EasyStepScanner;
